
// below is using primary color of app


import React, { useState, useMemo } from "react";
import { Modal, Input, Select, Empty, Typography, Space, Badge, theme } from "antd";
import {
  SearchOutlined,
  RightOutlined,
  DownOutlined,
  FolderOutlined,
  FileOutlined,
  TagOutlined,
} from "@ant-design/icons";
import Draggable from "react-draggable";

const { Text } = Typography;
// const { Option } = Select;
const { useToken } = theme;

// const SORT_OPTIONS = [
//   { label: "Therapeutic Area A–Z", value: "ta_asc" },
//   { label: "Therapeutic Area Z–A", value: "ta_desc" },
// ];

const highlightText = (text, term, primaryColor) => {
  if (!term) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(term.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <Text mark>{text.slice(idx, idx + term.length)}</Text>
      {text.slice(idx + term.length)}
    </>
  );
};

const LovModal = ({ visible, onClose, record }) => {
  const { token } = useToken();
  const primary = token.colorPrimary;
  const primaryBg = token.colorPrimaryBg;
  const primaryBgHover = token.colorPrimaryBgHover;
  const primaryBorder = token.colorPrimaryBorder;
  const primaryText = token.colorPrimaryText;

  const [disabled, setDisabled] = useState(true);
  const [bounds, setBounds] = useState({ left: 0, top: 0, bottom: 0, right: 0 });
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("ta_asc");
  const [openTAs, setOpenTAs] = useState({});
  const [openInds, setOpenInds] = useState({});
  const [openSubs, setOpenSubs] = useState({});
  const draggleRef = React.useRef(null);

  const onStart = (_event, uiData) => {
    const { clientWidth, clientHeight } = window.document.documentElement;
    const targetRect = draggleRef.current?.getBoundingClientRect();
    if (!targetRect) return;
    setBounds({
      left: -targetRect.left + uiData.x,
      right: clientWidth - (targetRect.right - uiData.x),
      top: -targetRect.top + uiData.y,
      bottom: clientHeight - (targetRect.bottom - uiData.y),
    });
  };

  const toggle = (setter, key) => setter(p => ({ ...p, [key]: !p[key] }));

  const isNested = useMemo(() => {
    const l = record?.lov_list;
    return Array.isArray(l) && l.length > 0 && typeof l[0] === "object" && l[0]?.therapeutic_area;
  }, [record]);

  const filteredData = useMemo(() => {
    if (!record) return [];
    if (!isNested) return record.lov_list ?? [];
    const term = search.toLowerCase();

    const data = record.lov_list
      .map(ta => ({
        ...ta,
        indications: ta.indications
          ?.map(ind => {
            // Case: indication has direct lov_list (no sub_indications)
            if (ind.lov_list) {
              const filtered = ind.lov_list.filter(v =>
                !term ||
                v.toLowerCase().includes(term) ||
                ind.indication.toLowerCase().includes(term) ||
                ta.therapeutic_area.toLowerCase().includes(term)
              );
              return filtered.length > 0 ? { ...ind, lov_list: filtered } : null;
            }
            // Case: indication has sub_indications
            const filteredSubs = (ind.sub_indications ?? [])
              .map(sub => ({
                ...sub,
                lov_list: sub.lov_list?.filter(v =>
                  !term ||
                  v.toLowerCase().includes(term) ||
                  sub.sub_indication.toLowerCase().includes(term) ||
                  ind.indication.toLowerCase().includes(term) ||
                  ta.therapeutic_area.toLowerCase().includes(term)
                ),
              }))
              .filter(sub => sub.lov_list?.length > 0);
            return filteredSubs.length > 0 ? { ...ind, sub_indications: filteredSubs } : null;
          })
          .filter(Boolean),
      }))
      .filter(ta => ta.indications?.length > 0);

    return [...data].sort((a, b) =>
      sortBy === "ta_asc"
        ? a.therapeutic_area.localeCompare(b.therapeutic_area)
        : b.therapeutic_area.localeCompare(a.therapeutic_area)
    );
  }, [record, isNested, search, sortBy]);
  const totalCount = useMemo(() => {
    if (!isNested)
      return filteredData.filter(v => !search || v.toLowerCase().includes(search.toLowerCase())).length;
    return filteredData.reduce(
      (acc, ta) =>
        acc +
        (ta.indications ?? []).reduce((a2, ind) => {
          if (ind.lov_list) return a2 + ind.lov_list.length;
          return a2 + (ind.sub_indications ?? []).reduce(
            (a3, sub) => a3 + (sub.lov_list?.length ?? 0), 0
          );
        }, 0),
      0
    );
  }, [filteredData, isNested, search]);

  const autoExpand = search.length > 0;

  const renderNested = () =>
    filteredData.map((ta, taIdx) => {
      const taKey = `ta_${taIdx}`;
      const taOpen = autoExpand || !!openTAs[taKey];

      return (
        <div
          key={taKey}
          style={{
            marginBottom: 8,
            border: `1px solid ${primaryBorder}`,
            borderRadius: token.borderRadius,
            overflow: "hidden",
          }}
        >
          {/* Level 1 – Therapeutic Area */}
          <div
            onClick={() => toggle(setOpenTAs, taKey)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "9px 14px",
              cursor: "pointer",
              borderBottom: taOpen ? `1px solid ${primaryBorder}` : "none",
            }}
          >
            <FolderOutlined style={{ color: primary, fontSize: 14 }} />
            <Text strong style={{ fontSize: 13, color: primaryText, flex: 1 }}>
              {highlightText(ta.therapeutic_area, search)}
            </Text>
            {taOpen
              ? <DownOutlined style={{ fontSize: 10, color: primary }} />
              : <RightOutlined style={{ fontSize: 10, color: primary }} />
            }
          </div>

          {taOpen &&
            (ta.indications ?? []).map((ind, indIdx) => {
              const indKey = `${taKey}_ind_${indIdx}`;
              const indOpen = autoExpand || !!openInds[indKey];

              return (
                <div key={indKey} style={{ borderBottom: `1px solid ${token.colorBorderSecondary}` }}>
                  {/* Level 2 – Indication */}
                  <div
                    onClick={() => toggle(setOpenInds, indKey)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 14px 8px 30px",
                      cursor: "pointer",
                      borderBottom: indOpen ? `1px solid ${token.colorBorderSecondary}` : "none",
                    }}
                  >
                    <FileOutlined style={{ color: primary, fontSize: 13, opacity: 0.8 }} />
                    <Text style={{ fontSize: 12.5, color: primaryText, flex: 1, fontWeight: 500 }}>
                      {highlightText(ind.indication, search)}
                    </Text>
                    {indOpen
                      ? <DownOutlined style={{ fontSize: 10, color: primary, opacity: 0.7 }} />
                      : <RightOutlined style={{ fontSize: 10, color: primary, opacity: 0.7 }} />
                    }
                  </div>

                  {/* Case A: indication has direct lov_list */}
                  {indOpen && ind.lov_list && (
                    <div style={{ padding: "8px 14px 8px 30px", background: token.colorBgLayout }}>
                      {ind.lov_list.map((val, vIdx) => (
                        <div
                          key={vIdx}
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 8,
                            padding: "4px 0",
                            borderBottom: vIdx < ind.lov_list.length - 1
                              ? `1px dashed ${token.colorBorderSecondary}`
                              : "none",
                          }}
                        >
                          <Badge
                            count={vIdx + 1}
                            style={{
                              backgroundColor: primaryBg,
                              color: primary,
                              fontSize: 10,
                              boxShadow: "none",
                              minWidth: 18,
                              height: 18,
                              lineHeight: "18px",
                              flexShrink: 0,
                              marginTop: 2,
                            }}
                          />
                          <Text style={{ fontSize: 12, color: token.colorText, lineHeight: "1.6" }}>
                            {highlightText(val, search)}
                          </Text>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Case B: indication has sub_indications */}
                  {indOpen && !ind.lov_list &&
                    (ind.sub_indications ?? []).map((sub, subIdx) => {
                      const subKey = `${indKey}_sub_${subIdx}`;
                      const subOpen = autoExpand || !!openSubs[subKey];

                      return (
                        <div key={subKey} style={{ borderBottom: `1px solid ${token.colorBorderSecondary}` }}>
                          {/* Level 3 – Sub-indication */}
                          <div
                            onClick={() => toggle(setOpenSubs, subKey)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "7px 14px 7px 48px",
                              background: token.colorBgContainer,
                              cursor: "pointer",
                              borderBottom: subOpen ? `1px solid ${token.colorBorderSecondary}` : "none",
                            }}
                          >
                            <TagOutlined style={{ color: primary, fontSize: 12, opacity: 0.6 }} />
                            <Text style={{ fontSize: 12, color: token.colorTextSecondary, flex: 1, fontWeight: 500 }}>
                              {highlightText(sub.sub_indication, search)}
                            </Text>
                            {subOpen
                              ? <DownOutlined style={{ fontSize: 10, color: token.colorTextTertiary }} />
                              : <RightOutlined style={{ fontSize: 10, color: token.colorTextTertiary }} />
                            }
                          </div>

                          {/* Level 4 – LOV items */}
                          {subOpen && (
                            <div style={{ padding: "8px 14px 8px 64px", background: token.colorBgLayout }}>
                              {sub.lov_list?.map((val, vIdx) => (
                                <div
                                  key={vIdx}
                                  style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 8,
                                    padding: "4px 0",
                                    borderBottom: vIdx < sub.lov_list.length - 1
                                      ? `1px dashed ${token.colorBorderSecondary}`
                                      : "none",
                                  }}
                                >
                                  <Badge
                                    count={vIdx + 1}
                                    style={{
                                      backgroundColor: primaryBg,
                                      color: primary,
                                      fontSize: 10,
                                      boxShadow: "none",
                                      minWidth: 18,
                                      height: 18,
                                      lineHeight: "18px",
                                      flexShrink: 0,
                                      marginTop: 2,
                                    }}
                                  />
                                  <Text style={{ fontSize: 12, color: token.colorText, lineHeight: "1.6" }}>
                                    {highlightText(val, search)}
                                  </Text>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              );
            })}
        </div>
      );
    });

  const renderFlat = () => {
    const list = filteredData
      .filter(v => !search || v.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => sortBy === "ta_asc" ? a.localeCompare(b) : b.localeCompare(a));

    return list.map((val, idx) => (
      <div
        key={idx}
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          padding: "6px 0",
          borderBottom: idx < list.length - 1 ? `1px solid ${token.colorBorderSecondary}` : "none",
        }}
      >
        <Badge
          count={idx + 1}
          style={{
            backgroundColor: primaryBg,
            color: primary,
            fontSize: 10,
            boxShadow: "none",
            minWidth: 18,
            height: 18,
            lineHeight: "18px",
            flexShrink: 0,
            marginTop: 2,
          }}
        />
        <Text style={{ fontSize: 13, color: token.colorText, lineHeight: "1.6" }}>
          {highlightText(val, search)}
        </Text>
      </div>
    ));
  };

  const handleClose = () => {
    setSearch("");
    setOpenTAs({});
    setOpenInds({});
    setOpenSubs({});
    onClose();
  };

  return (
    <Modal
      title={
        <div
          style={{ width: "100%", cursor: "move" }}
          onMouseOver={() => { if (disabled) setDisabled(false); }}
          onMouseOut={() => setDisabled(true)}
        >
          <Space size={8}>
            <span>List of Values</span>
            {record?.ques_alias && (
              <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                {record.ques_alias}
              </Text>
            )}
          </Space>
        </div>
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={820}
      destroyOnClose
      maskClosable={false}
      modalRender={(modal) => (
        <Draggable
          disabled={disabled}
          bounds={bounds}
          nodeRef={draggleRef}
          onStart={(event, uiData) => onStart(event, uiData)}
        >
          <div ref={draggleRef}>{modal}</div>
        </Draggable>
      )}
    >
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, alignItems: "center" }}>
        <Input
          prefix={<SearchOutlined style={{ color: token.colorTextPlaceholder }} />}
          placeholder="Search values, indications, therapeutic areas..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          allowClear
          style={{ flex: 1 }}
          size="large"
        />
        {/* <Select value={sortBy} onChange={setSortBy} style={{ width: 210 }} size="large">
          {SORT_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
        </Select> */}
      </div>

      {/* Content */}
      <div style={{ maxHeight: 520, overflowY: "auto", paddingRight: 2 }}>
        {totalCount === 0 ? (
          <Empty description="No results found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : isNested ? (
          renderNested()
        ) : (
          renderFlat()
        )}
      </div>
    </Modal>
  );
};

export default LovModal;
















// ta is replaced with indication code and breadcrumb

// import React, { useState, useMemo } from "react";
// import { Modal, Input, Select, Empty, Typography, Space, Badge, Breadcrumb, theme } from "antd";
// import {
//   SearchOutlined,
//   RightOutlined,
//   DownOutlined,
//   FolderOutlined,
//   FileOutlined,
//   TagOutlined,
//   LeftOutlined,
// } from "@ant-design/icons";
// import Draggable from "react-draggable";

// const { Text } = Typography;
// const { Option } = Select;
// const { useToken } = theme;

// const SORT_OPTIONS = [
//   { label: "Therapeutic Area A–Z", value: "ta_asc" },
//   { label: "Therapeutic Area Z–A", value: "ta_desc" },
// ];

// const highlightText = (text, term) => {
//   if (!term) return <>{text}</>;
//   const idx = text.toLowerCase().indexOf(term.toLowerCase());
//   if (idx === -1) return <>{text}</>;
//   return (
//     <>
//       {text.slice(0, idx)}
//       <Text mark>{text.slice(idx, idx + term.length)}</Text>
//       {text.slice(idx + term.length)}
//     </>
//   );
// };

// const LovModal = ({ visible, onClose, record }) => {
//   const { token } = useToken();
//   const primary = token.colorPrimary;
//   const primaryBg = token.colorPrimaryBg;
//   const primaryBgHover = token.colorPrimaryBgHover;
//   const primaryBorder = token.colorPrimaryBorder;
//   const primaryText = token.colorPrimaryText;

//   const [disabled, setDisabled] = useState(true);
//   const [bounds, setBounds] = useState({ left: 0, top: 0, bottom: 0, right: 0 });
//   const [search, setSearch] = useState("");
//   const [sortBy, setSortBy] = useState("ta_asc");
//   const [openInds, setOpenInds] = useState({});
//   const [openSubs, setOpenSubs] = useState({});

//   // drill-down state: null = root, { ta } = inside a TA
//   const [selectedTA, setSelectedTA] = useState(null);

//   const draggleRef = React.useRef(null);

//   const onStart = (_event, uiData) => {
//     const { clientWidth, clientHeight } = window.document.documentElement;
//     const targetRect = draggleRef.current?.getBoundingClientRect();
//     if (!targetRect) return;
//     setBounds({
//       left: -targetRect.left + uiData.x,
//       right: clientWidth - (targetRect.right - uiData.x),
//       top: -targetRect.top + uiData.y,
//       bottom: clientHeight - (targetRect.bottom - uiData.y),
//     });
//   };

//   const toggle = (setter, key) => setter(p => ({ ...p, [key]: !p[key] }));

//   const isNested = useMemo(() => {
//     const l = record?.lov_list;
//     return Array.isArray(l) && l.length > 0 && typeof l[0] === "object" && l[0]?.therapeutic_area;
//   }, [record]);

//   const filteredTAs = useMemo(() => {
//     if (!record || !isNested) return [];
//     const term = search.toLowerCase();

//     let data = record.lov_list
//       .map(ta => ({
//         ...ta,
//         indications: ta.indications
//           ?.map(ind => ({
//             ...ind,
//             sub_indications: ind.sub_indications
//               ?.map(sub => ({
//                 ...sub,
//                 lov_list: sub.lov_list?.filter(v =>
//                   !term ||
//                   v.toLowerCase().includes(term) ||
//                   sub.sub_indication.toLowerCase().includes(term) ||
//                   ind.indication.toLowerCase().includes(term) ||
//                   ta.therapeutic_area.toLowerCase().includes(term)
//                 ),
//               }))
//               .filter(sub => sub.lov_list?.length > 0),
//           }))
//           .filter(ind => ind.sub_indications?.length > 0),
//       }))
//       .filter(ta => ta.indications?.length > 0);

//     return [...data].sort((a, b) =>
//       sortBy === "ta_asc"
//         ? a.therapeutic_area.localeCompare(b.therapeutic_area)
//         : b.therapeutic_area.localeCompare(a.therapeutic_area)
//     );
//   }, [record, isNested, search, sortBy]);

//   const flatList = useMemo(() => {
//     if (!record || isNested) return [];
//     return (record.lov_list ?? [])
//       .filter(v => !search || v.toLowerCase().includes(search.toLowerCase()))
//       .sort((a, b) => sortBy === "ta_asc" ? a.localeCompare(b) : b.localeCompare(a));
//   }, [record, isNested, search, sortBy]);

//   const autoExpand = search.length > 0;

//   const handleSelectTA = (ta) => {
//     setSelectedTA(ta);
//     const allInds = {};
//     (ta.indications ?? []).forEach((_, i) => { allInds[`ind_${i}`] = true; });
//     setOpenInds(allInds);
//     setOpenSubs({});
//   };

//   const handleBack = () => {
//     setSelectedTA(null);
//     setOpenInds({});
//     setOpenSubs({});
//   };

//   const handleClose = () => {
//     setSearch("");
//     setSelectedTA(null);
//     setOpenInds({});
//     setOpenSubs({});
//     onClose();
//   };

//   // root level: list of therapeutic areas
//   const renderTAList = () => (
//     <div>
//       {filteredTAs.length === 0
//         ? <Empty description="No results found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
//         : filteredTAs.map((ta, taIdx) => (
//           <div
//             key={taIdx}
//             onClick={() => handleSelectTA(ta)}
//             style={{
//               display: "flex",
//               alignItems: "center",
//               gap: 10,
//               padding: "11px 14px",
//               marginBottom: 6,
//               border: `1px solid ${primaryBorder}`,
//               borderRadius: token.borderRadius,
//               background: token.colorBgContainer,
//               cursor: "pointer",
//               transition: "background 0.15s",
//             }}
//             onMouseEnter={e => e.currentTarget.style.background = primaryBg}
//             onMouseLeave={e => e.currentTarget.style.background = token.colorBgContainer}
//           >
//             <FolderOutlined style={{ color: primary, fontSize: 15 }} />
//             <Text strong style={{ fontSize: 13, color: primaryText, flex: 1 }}>
//               {highlightText(ta.therapeutic_area, search)}
//             </Text>
//             <RightOutlined style={{ fontSize: 11, color: primary }} />
//           </div>
//         ))
//       }
//     </div>
//   );

//   // drill-down level: indications + sub-indications inside selected TA
//   const renderIndications = () => {
//     const indications = selectedTA?.indications ?? [];
//     if (indications.length === 0)
//       return <Empty description="No results found" image={Empty.PRESENTED_IMAGE_SIMPLE} />;

//     return indications.map((ind, indIdx) => {
//       const indKey = `ind_${indIdx}`;
//       const indOpen = autoExpand || !!openInds[indKey];

//       return (
//         <div
//           key={indKey}
//           style={{
//             marginBottom: 8,
//             border: `1px solid ${token.colorBorderSecondary}`,
//             borderRadius: token.borderRadius,
//             overflow: "hidden",
//           }}
//         >
//           {/* Indication header */}
//           <div
//             onClick={() => toggle(setOpenInds, indKey)}
//             style={{
//               display: "flex",
//               alignItems: "center",
//               gap: 8,
//               padding: "9px 14px",
//               // background: token.colorPrimaryBgHover,
//               cursor: "pointer",
//               borderBottom: indOpen ? `1px solid ${token.colorBorderSecondary}` : "none",
//             }}
//           >
//             <FileOutlined style={{ color: primary, fontSize: 13 }} />
//             <Text style={{ fontSize: 13, color: primaryText, flex: 1, fontWeight: 500 }}>
//               {highlightText(ind.indication, search)}
//             </Text>
//             {indOpen
//               ? <DownOutlined style={{ fontSize: 10, color: primary }} />
//               : <RightOutlined style={{ fontSize: 10, color: primary }} />
//             }
//           </div>

//           {indOpen &&
//             (ind.sub_indications ?? []).map((sub, subIdx) => {
//               const subKey = `${indKey}_sub_${subIdx}`;
//               const subOpen = autoExpand || !!openSubs[subKey];

//               return (
//                 <div key={subKey} style={{ borderBottom: `1px solid ${token.colorBorderSecondary}` }}>
//                   {/* Sub-indication header */}
//                   <div
//                     onClick={() => toggle(setOpenSubs, subKey)}
//                     style={{
//                       display: "flex",
//                       alignItems: "center",
//                       gap: 8,
//                       padding: "7px 14px 7px 32px",
//                       background: token.colorBgContainer,
//                       cursor: "pointer",
//                       borderBottom: subOpen ? `1px solid ${token.colorBorderSecondary}` : "none",
//                     }}
//                   >
//                     <TagOutlined style={{ color: primary, fontSize: 12, opacity: 0.7 }} />
//                     <Text style={{ fontSize: 12, color: token.colorTextSecondary, flex: 1, fontWeight: 500 }}>
//                       {highlightText(sub.sub_indication, search)}
//                     </Text>
//                     {subOpen
//                       ? <DownOutlined style={{ fontSize: 10, color: token.colorTextTertiary }} />
//                       : <RightOutlined style={{ fontSize: 10, color: token.colorTextTertiary }} />
//                     }
//                   </div>

//                   {/* LOV items */}
//                   {subOpen && (
//                     <div style={{ padding: "8px 14px 8px 48px", background: token.colorBgLayout }}>
//                       {sub.lov_list?.map((val, vIdx) => (
//                         <div
//                           key={vIdx}
//                           style={{
//                             display: "flex",
//                             alignItems: "flex-start",
//                             gap: 8,
//                             padding: "4px 0",
//                             borderBottom: vIdx < sub.lov_list.length - 1
//                               ? `1px dashed ${token.colorBorderSecondary}`
//                               : "none",
//                           }}
//                         >
//                           <Badge
//                             count={vIdx + 1}
//                             style={{
//                               backgroundColor: primaryBg,
//                               color: primary,
//                               fontSize: 10,
//                               boxShadow: "none",
//                               minWidth: 18,
//                               height: 18,
//                               lineHeight: "18px",
//                               flexShrink: 0,
//                               marginTop: 2,
//                             }}
//                           />
//                           <Text style={{ fontSize: 12, color: token.colorText, lineHeight: "1.6" }}>
//                             {highlightText(val, search)}
//                           </Text>
//                         </div>
//                       ))}
//                     </div>
//                   )}
//                 </div>
//               );
//             })}
//         </div>
//       );
//     });
//   };

//   const renderFlat = () =>
//     flatList.length === 0
//       ? <Empty description="No results found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
//       : flatList.map((val, idx) => (
//         <div
//           key={idx}
//           style={{
//             display: "flex",
//             alignItems: "flex-start",
//             gap: 10,
//             padding: "6px 0",
//             borderBottom: idx < flatList.length - 1 ? `1px solid ${token.colorBorderSecondary}` : "none",
//           }}
//         >
//           <Badge
//             count={idx + 1}
//             style={{
//               backgroundColor: primaryBg,
//               color: primary,
//               fontSize: 10,
//               boxShadow: "none",
//               minWidth: 18,
//               height: 18,
//               lineHeight: "18px",
//               flexShrink: 0,
//               marginTop: 2,
//             }}
//           />
//           <Text style={{ fontSize: 13, color: token.colorText, lineHeight: "1.6" }}>
//             {highlightText(val, search)}
//           </Text>
//         </div>
//       ));

//   return (
//     <Modal
//       title={
//         <div
//           style={{ width: "100%", cursor: "move" }}
//           onMouseOver={() => { if (disabled) setDisabled(false); }}
//           onMouseOut={() => setDisabled(true)}
//         >
//           <Space size={8}>
//             <span>List of Values</span>
//             {record?.ques_alias && (
//               <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
//                 — {record.ques_alias}
//               </Text>
//             )}
//           </Space>
//         </div>
//       }
//       open={visible}
//       onCancel={handleClose}
//       footer={null}
//       width={820}
//       destroyOnClose
//       maskClosable={false}
//       modalRender={(modal) => (
//         <Draggable
//           disabled={disabled}
//           bounds={bounds}
//           nodeRef={draggleRef}
//           onStart={(event, uiData) => onStart(event, uiData)}
//         >
//           <div ref={draggleRef}>{modal}</div>
//         </Draggable>
//       )}
//     >
//       {/* Toolbar */}
//       <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center" }}>
//         <Input
//           prefix={<SearchOutlined style={{ color: token.colorTextPlaceholder }} />}
//           placeholder="Search values, indications, therapeutic areas..."
//           value={search}
//           onChange={e => { setSearch(e.target.value); setSelectedTA(null); }}
//           allowClear
//           style={{ flex: 1 }}
//           size="large"
//         />
//         {!selectedTA && (
//           <Select value={sortBy} onChange={setSortBy} style={{ width: 210 }} size="large">
//             {SORT_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
//           </Select>
//         )}
//       </div>

//       {/* Breadcrumb */}
//       {isNested && (
//         <div style={{ marginBottom: 10 }}>
//           <Breadcrumb
//             items={[
//               {
//                 title: selectedTA ? (
//                   <span
//                     onClick={handleBack}
//                     style={{ color: primary, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
//                   >
//                     <LeftOutlined style={{ fontSize: 10 }} /> All Therapeutic Areas
//                   </span>
//                 ) : (
//                   <span style={{ color: token.colorTextSecondary }}>All Therapeutic Areas</span>
//                 ),
//               },
//               ...(selectedTA ? [{ title: <Text strong style={{ color: primaryText }}>{selectedTA.therapeutic_area}</Text> }] : []),
//             ]}
//           />
//         </div>
//       )}

//       {/* Content */}
//       <div style={{ maxHeight: 520, overflowY: "auto", paddingRight: 2 }}>
//         {!isNested
//           ? renderFlat()
//           : selectedTA
//           ? renderIndications()
//           : renderTAList()
//         }
//       </div>
//     </Modal>
//   );
// };

// export default LovModal;